import axiosInstance from '../utils/axiosConfig';

// Patch: Prevent double /api in production
let API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
if (API_URL === '/api') {
  API_URL = '';
}

// Cache store for API responses - separate caches for partner mode and non-partner mode
const cache = {
  expenses: null,
  expenseStats: null,
  lastFetchTime: 0,
  cacheExpiry: 60000, // Cache expires after 1 minute
  // Partner mode cache
  partnerExpenses: null,
  partnerExpenseStats: null,
  partnerLastFetchTime: 0
};

/**
 * Expense Service
 * 
 * Handles all expense-related operations and data fetching
 */
const expenseService = {
  /**
   * Clear all caches (useful when switching between partner/personal modes)
   */
  clearCache() {
    cache.expenses = null;
    cache.expenseStats = null;
    cache.partnerExpenses = null;
    cache.partnerExpenseStats = null;
    cache.lastFetchTime = 0;
    cache.partnerLastFetchTime = 0;
    console.log('🗑️ Expense service cache cleared');
  },

  /**
   * Get authentication header with JWT token
   * @returns {Object} Headers object with Authorization
   */
  getAuthHeader() {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  },

  /**
   * Get all expenses for the current user with caching
   * @returns {Promise} Promise with expenses data
   */
  async getAllExpenses(force = false, isPartnerMode = false, isOurMode = false) {
    try {
      // Check if we have cached data and if it's still valid - use mode-specific cache
      const now = Date.now();
      let cachedData, lastFetch;
      const mode = isOurMode ? 'our' : (isPartnerMode ? 'partner' : 'personal');
      
      if (isOurMode) {
        cachedData = cache.ourExpenses;
        lastFetch = cache.ourLastFetchTime;
      } else if (isPartnerMode) {
        cachedData = cache.partnerExpenses;
        lastFetch = cache.partnerLastFetchTime;
      } else {
        cachedData = cache.expenses;
        lastFetch = cache.lastFetchTime;
      }
      
      const cacheAge = now - lastFetch;
      const hasValidCache = !force && cachedData && (cacheAge < cache.cacheExpiry);
      
      if (hasValidCache) {
        console.log(`Using cached expenses data (${mode})`);
        return cachedData;
      }

      // Build URL with appropriate query parameter
      let url = `/api/cashflow/expenses`;
      if (isPartnerMode) {
        url += `?partner=true`;
      } else if (isOurMode) {
        url += `?includePartner=true`;
      }
      
      const response = await axiosInstance.get(url, this.getAuthHeader());
      
      // Normalize to plain array (server may wrap in { success, data })
      const data = response.data?.data ?? response.data ?? [];
      const expenses = Array.isArray(data) ? data : [];

      // Update mode-specific cache
      if (isOurMode) {
        cache.ourExpenses = expenses;
        cache.ourLastFetchTime = now;
      } else if (isPartnerMode) {
        cache.partnerExpenses = expenses;
        cache.partnerLastFetchTime = now;
      } else {
        cache.expenses = expenses;
        cache.lastFetchTime = now;
      }
      
      return expenses;
    } catch (error) {
      console.error('Error fetching expenses:', error);
      // If we have cached data, return it even if expired
      const fallbackCache = isPartnerMode ? cache.partnerExpenses : cache.expenses;
      if (fallbackCache) {
        console.log(`Using expired cached data for expenses (${isPartnerMode ? 'partner' : 'personal'})`);
        return fallbackCache;
      }
      throw error;
    }
  },

  /**
   * Get an expense by its ID
   * @param {string} expenseId - The expense ID to retrieve
   * @returns {Promise} Promise with expense data
   */
  async getExpenseById(expenseId) {
    try {
      // First check if we have this expense in our cache
      if (cache.expenses) {
        const cachedExpense = cache.expenses.find(expense => expense._id === expenseId);
        if (cachedExpense) {
          return cachedExpense;
        }
      }
      
      const response = await axiosInstance.get(`/api/cashflow/expenses/${expenseId}`, this.getAuthHeader());
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`Error fetching expense ${expenseId}:`, error);
      throw error;
    }
  },

  /**
   * Create a new expense
   * @param {Object} expenseData - The expense data to create
   * @returns {Promise} Promise with created expense data
   */
  async createExpense(expenseData) {
    try {
      const response = await axiosInstance.post(`/api/cashflow/expenses`, expenseData, this.getAuthHeader());
      
      // Invalidate cache
      cache.expenses = null;
      cache.expenseStats = null;
      
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error creating expense:', error);
      throw error;
    }
  },

  /**
   * Update an expense
   * @param {string} expenseId - The expense ID to update
   * @param {Object} expenseData - The updated expense data
   * @returns {Promise} Promise with updated expense data
   */
  async updateExpense(expenseId, expenseData) {
    try {
      const response = await axiosInstance.put(
        `/api/cashflow/expenses/${expenseId}`, 
        expenseData, 
        this.getAuthHeader()
      );
      
      // Invalidate cache
      cache.expenses = null;
      cache.expenseStats = null;
      
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`Error updating expense ${expenseId}:`, error);
      throw error;
    }
  },

  /**
   * Delete an expense
   * @param {string} expenseId - The expense ID to delete
   * @returns {Promise} Promise with deletion result
   */
  async deleteExpense(expenseId) {
    try {
      const response = await axiosInstance.delete(
        `/api/cashflow/expenses/${expenseId}`, 
        this.getAuthHeader()
      );
      
      // Invalidate cache
      cache.expenses = null;
      cache.expenseStats = null;
      
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`Error deleting expense ${expenseId}:`, error);
      throw error;
    }
  },

  /**
   * Record a payment for an expense
   * @param {string} expenseId - The expense ID
   * @param {Object} paymentData - Data about the payment
   * @returns {Promise} Promise with updated expense data
   */
  async recordPayment(expenseId, paymentData) {
    try {
      const response = await axiosInstance.post(
        `/api/cashflow/expenses/${expenseId}/payments`,
        paymentData,
        this.getAuthHeader()
      );
      
      // Invalidate cache
      cache.expenses = null;
      cache.expenseStats = null;
      
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`Error recording payment to expense ${expenseId}:`, error);
      throw error;
    }
  },

  /**
   * Get expense statistics for dashboard with caching
   * @returns {Promise} Promise with expense stats data
   */
  async getExpenseStatistics(isPartnerMode = false, isOurMode = false) {
    try {
      // Check if we have cached stats and if they're still valid - use mode-specific cache
      const now = Date.now();
      let cachedStats, lastFetch;
      const mode = isOurMode ? 'our' : (isPartnerMode ? 'partner' : 'personal');
      
      if (isOurMode) {
        cachedStats = cache.ourExpenseStats;
        lastFetch = cache.ourLastFetchTime;
      } else if (isPartnerMode) {
        cachedStats = cache.partnerExpenseStats;
        lastFetch = cache.partnerLastFetchTime;
      } else {
        cachedStats = cache.expenseStats;
        lastFetch = cache.lastFetchTime;
      }
      
      if (cachedStats && (now - lastFetch < cache.cacheExpiry)) {
        console.log(`Using cached expense stats (${mode})`);
        return cachedStats;
      }

      // Fetch all expenses (this will use its own cache if available)
      let expenses = await this.getAllExpenses(isPartnerMode, isOurMode);
      if (!Array.isArray(expenses)) expenses = [];
      
      if (expenses.length === 0) {
        const emptyStats = {
          totalMonthly: 0,
          fixedExpenses: 0,
          variableExpenses: 0,
          fixedRatio: 0,
          topCategory: { name: 'None', amount: 0 },
          overdueCount: 0,
          averageAmount: 0,
          upcomingPayments: []
        };
        cache.expenseStats = emptyStats;
        return emptyStats;
      }
      
      // Calculate total monthly expenses
      const totalMonthly = expenses.reduce((sum, expense) => {
        if (expense.frequency === 'monthly') {
          return sum + (expense.amount || 0);
        } else if (expense.frequency === 'weekly') {
          return sum + ((expense.amount || 0) * 4.33);
        } else if (expense.frequency === 'bi-weekly') {
          return sum + ((expense.amount || 0) * 2.17);
        } else if (expense.frequency === 'annually') {
          return sum + ((expense.amount || 0) / 12);
        } else if (expense.frequency === 'quarterly') {
          return sum + ((expense.amount || 0) / 3);
        }
        return sum + (expense.amount || 0); // Default to adding as-is
      }, 0);
      
      // Calculate fixed and variable expenses
      const fixedExpenses = expenses
        .filter(expense => expense.type === 'fixed')
        .reduce((sum, expense) => {
          if (expense.frequency === 'monthly') {
            return sum + (expense.amount || 0);
          } else if (expense.frequency === 'weekly') {
            return sum + ((expense.amount || 0) * 4.33);
          } else if (expense.frequency === 'bi-weekly') {
            return sum + ((expense.amount || 0) * 2.17);
          } else if (expense.frequency === 'annually') {
            return sum + ((expense.amount || 0) / 12);
          } else if (expense.frequency === 'quarterly') {
            return sum + ((expense.amount || 0) / 3);
          }
          return sum + (expense.amount || 0); // Default to adding as-is
        }, 0);
      
      const variableExpenses = expenses
        .filter(expense => expense.type === 'variable')
        .reduce((sum, expense) => {
          if (expense.frequency === 'monthly') {
            return sum + (expense.amount || 0);
          } else if (expense.frequency === 'weekly') {
            return sum + ((expense.amount || 0) * 4.33);
          } else if (expense.frequency === 'bi-weekly') {
            return sum + ((expense.amount || 0) * 2.17);
          } else if (expense.frequency === 'annually') {
            return sum + ((expense.amount || 0) / 12);
          } else if (expense.frequency === 'quarterly') {
            return sum + ((expense.amount || 0) / 3);
          }
          return sum + (expense.amount || 0); // Default to adding as-is
        }, 0);
      
      // Calculate fixed to variable ratio
      const fixedRatio = totalMonthly > 0 ? (fixedExpenses / totalMonthly) * 100 : 0;
      
      // Find top spending category
      const categoryMap = {};
      expenses.forEach(expense => {
        const category = expense.category || 'Uncategorized';
        const monthlyAmount = (() => {
          if (expense.frequency === 'monthly') return expense.amount || 0;
          if (expense.frequency === 'weekly') return (expense.amount || 0) * 4.33;
          if (expense.frequency === 'bi-weekly') return (expense.amount || 0) * 2.17;
          if (expense.frequency === 'annually') return (expense.amount || 0) / 12;
          if (expense.frequency === 'quarterly') return (expense.amount || 0) / 3;
          return expense.amount || 0;
        })();
        
        categoryMap[category] = (categoryMap[category] || 0) + monthlyAmount;
      });
      
      let topCategory = { name: 'None', amount: 0 };
      
      Object.entries(categoryMap).forEach(([name, amount]) => {
        if (amount > topCategory.amount) {
          topCategory = { name, amount };
        }
      });
      
      // Count overdue expenses
      const today = new Date();
      const overdueCount = expenses.filter(expense => {
        if (!expense.dueDate) return false;
        const dueDate = new Date(expense.dueDate);
        return dueDate < today && (!expense.isPaid);
      }).length;
      
      // Calculate average expense amount
      const averageAmount = expenses.length > 0 
        ? totalMonthly / expenses.length 
        : 0;
      
      // Get upcoming payments (due in the next 30 days)
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      
      const upcomingPayments = expenses
        .filter(expense => {
          if (!expense.dueDate || expense.isPaid) return false;
          const dueDate = new Date(expense.dueDate);
          return dueDate >= today && dueDate <= thirtyDaysFromNow;
        })
        .map(expense => ({
          id: expense._id,
          name: expense.name,
          amount: expense.amount,
          dueDate: expense.dueDate,
          type: 'expense'
        }))
        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
      
      const expenseStats = {
        totalMonthly,
        fixedExpenses,
        variableExpenses,
        fixedRatio,
        topCategory,
        overdueCount,
        averageAmount,
        upcomingPayments
      };
      
      // Update mode-specific cache
      if (isPartnerMode) {
        cache.partnerExpenseStats = expenseStats;
      } else {
        cache.expenseStats = expenseStats;
      }
      
      return expenseStats;
    } catch (error) {
      console.error('Error fetching expense statistics:', error);
      
      // If we have cached stats, return them even if expired
      const fallbackStats = isPartnerMode ? cache.partnerExpenseStats : cache.expenseStats;
      if (fallbackStats) {
        console.log(`Using expired cached expense stats (${isPartnerMode ? 'partner' : 'personal'})`);
        return fallbackStats;
      }
      
      // Return default values as a last resort
      return {
        totalMonthly: 0,
        fixedExpenses: 0,
        variableExpenses: 0,
        fixedRatio: 0,
        topCategory: { name: 'None', amount: 0 },
        overdueCount: 0,
        averageAmount: 0,
        upcomingPayments: []
      };
    }
  }
};

export default expenseService; 